import { action, computed, makeObservable, observable } from 'mobx'

import {
  DefaultLandingPage,
  ILastUpdatedItem,
  IMutation,
  IProjectAddressInput,
  IProjectClosedInterval,
  IProjectHistory,
  IProjectInput,
  IProjectTypeOptionsInput,
  IWorkingHoursInput,
} from '~/client/graph'
import { SaveProjectsDataDocument } from '~/client/graph/operations/generated/Project.generated'
import ISaveProjectSuccessOptions from '~/client/src/desktop/interfaces/ISaveProjectSuccessOptions'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import setUpPagesNames from '~/client/src/desktop/utils/setUpPagesNames'
import ProjectSetUpPage from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPages'
import commonRoutes, {
  getTransitionPath,
} from '~/client/src/shared/constants/commonRoutes'
import DateTimeFormat from '~/client/src/shared/enums/DateTimeFormat'
import { IMapBoxFeature } from '~/client/src/shared/interfaces/IMapBoxFeature'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Project, {
  DEFAULT_WORK_FINISH_HOUR,
  DEFAULT_WORK_START_HOUR,
} from '~/client/src/shared/models/Project'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { EMPTY_OBJECT_ID } from '~/client/src/shared/utils/GraphExecutor'
import {
  buildProjectSchedule,
  getProjectScheduleMode,
  getScheduleWorkingDays,
  IProjectDaySchedule,
  ProjectScheduleMode,
  toProjectScheduleInput,
  withDayHours,
  withEverydayHours,
  withEverydayWeek,
  withToggledWorkingDay,
} from '~/client/src/shared/utils/projectSchedule'
import { WITHOUT_SPECIAL_CHARACTERS_PATTERN } from '~/client/src/shared/utils/regExpPatterns'
import {
  defaultTimezoneId,
  getTimezoneIdByCoordinates,
} from '~/client/src/shared/utils/timezones'
import { toast } from '~/client/src/shared/utils/toast'
import { customDebounce } from '~/client/src/shared/utils/util'

import ProjectSetUpPageStore, {
  ProjectDetailsTabs,
} from '../../ProjectSetUpPage.store'

const PAGE_NAME = setUpPagesNames[ProjectSetUpPage.PROJECT_DETAILS]

const projectNameValidationRegex = /^[A-Za-z0-9\-_ ]+$/
const projectCodeMaxLength = 5
const SCHEDULE_SAVE_DEBOUNCE_MS = 400

export type ResourceApp =
  | 'locations'
  | 'equipment'
  | 'materialResources'
  | 'struxTags'

export default class ProjectSettingsStore {
  @observable public isNewProjectSelected: boolean = false
  @observable public isProjectErrorMessageVisible: boolean = false
  @observable public isProjectCodeErrorMessageVisible: boolean = false
  @observable public projectName: string = ''
  @observable public maturixToken: string = ''
  @observable public projectCode: string = ''
  @observable public projectLogoUrl: string = ''
  @observable public projectHeaderImageUrl: string = ''
  @observable public projectAddress: IProjectAddressInput = null
  @observable public closedIntervals: IProjectClosedInterval[] = []
  // .ref: every write replaces the array wholesale from the pure helpers in
  // projectSchedule.ts, so deep observability would only re-wrap rows nobody
  // mutates in place.
  @observable.ref public daySchedules: IProjectDaySchedule[] = []
  @observable public scheduleMode: ProjectScheduleMode =
    ProjectScheduleMode.Custom
  /** Project the loaded `daySchedules` belong to — see `seedSchedule`. */
  private scheduleProjectId: string = null
  @observable public defaultLandingPage: DefaultLandingPage =
    DefaultLandingPage.Home
  @observable public isDeliveriesEnabled: boolean = false
  @observable public isAnalyticsEnabled: boolean = true
  @observable public isTrackerEnabled: boolean = false
  @observable public isLogisticsEnabled: boolean = true
  @observable public isMaterialsEnabled: boolean = false
  @observable public isMaterialResourcesEnabled: boolean = false
  @observable public isEquipmentEnabled: boolean = false
  @observable public isPhotosEnabled: boolean = true
  @observable public isFormsEnabled: boolean = true
  @observable public isStruxTagEnabled: boolean = false
  @observable public isLocationsEnabled: boolean = false

  @observable public isConfirmationModalDisplayed: boolean = false
  @observable public preConfimedTimezoneId: string = ''
  @observable public timezoneId: string = ''
  @observable public dateTimeFormat: DateTimeFormat = DateTimeFormat.Default
  @observable public projectAddressSuggestions: IMapBoxFeature[] = []

  public constructor(
    private readonly projectsStore: ProjectsStore,
    private readonly eventsStore: DesktopEventStore,
    private readonly projectSetUpPageStore: ProjectSetUpPageStore,
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly projectDateStore: ProjectDateStore,
  ) {
    makeObservable(this)
  }

  public get activeProject() {
    return this.projectsStore.activeProject
  }

  public get activeTab(): ProjectDetailsTabs {
    return this.projectSetUpPageStore.activeTab
  }

  @action.bound
  public setActiveTab(tab: ProjectDetailsTabs) {
    if (this.isTabDisabled(tab)) {
      return
    }
    this.projectSetUpPageStore.activeTab = tab
  }

  public isTabDisabled = (tab: ProjectDetailsTabs): boolean => {
    switch (tab) {
      case ProjectDetailsTabs.Apps:
        return this.isNewProjectSelected
      case ProjectDetailsTabs.QRCodes:
        const { isWorkflowsDisabled, isLogisticsDisabled } =
          this.eventsStore.appState
        const shouldRenderQRCodes =
          (!isWorkflowsDisabled || !isLogisticsDisabled) &&
          !this.isNewProjectSelected
        return !shouldRenderQRCodes
      default:
        return false
    }
  }

  public get projects() {
    return this.projectsStore.list
  }

  private get isEditMode(): boolean {
    return !this.isNewProjectSelected && !!this.activeProject
  }

  private get defaultWorkingHours(): IWorkingHoursInput {
    return {
      startTime: this.projectDateStore
        .setHours(new Date(), DEFAULT_WORK_START_HOUR, 0)
        .getTime(),
      endTime: this.projectDateStore
        .setHours(new Date(), DEFAULT_WORK_FINISH_HOUR, 0)
        .getTime(),
    }
  }

  private get lastUpdated(): ILastUpdatedItem {
    const {
      id,
      email,
      firstName = null,
      lastName = null,
    } = this.eventsStore.appState.user

    return {
      pageName: PAGE_NAME,
      updateInfo: {
        updatedAt: Date.now(),
        by: { id, email, firstName, lastName },
      },
    }
  }

  private get updatedFields(): {
    name: string
    code: string
    logoUrl: string
    timezoneId: string
    dateTimeFormat: DateTimeFormat
    defaultLandingPage: DefaultLandingPage
  } {
    return {
      name: this.projectName,
      code: this.projectCode,
      logoUrl: this.projectLogoUrl,
      timezoneId: this.timezoneId,
      dateTimeFormat: this.dateTimeFormat,
      defaultLandingPage: this.defaultLandingPage,
    }
  }

  public onProjectUpdate = (eventContext: EventContext) => {
    const [eventType] = eventContext.event

    if (eventType === e.PROJECT_RECEIVED) {
      this.setProjectDataValues()
    }
  }

  public toggleConfirmationModal = () => {
    this.isConfirmationModalDisplayed = !this.isConfirmationModalDisplayed
  }

  public confirmTimzoneChange = () => {
    this.timezoneId = this.preConfimedTimezoneId

    if (this.isEditMode) {
      this.updateProject()
    } else {
      this.toggleConfirmationModal()
    }
  }

  @action
  public setProjectDataValues() {
    this.clearForm()
    if (!this.activeProject) {
      return
    }

    const {
      code: projectCode = '',
      name: projectName = '',
      logoUrl: projectLogoUrl = '',
      headerImageUrl: projectHeaderImageUrl = '',
      timezoneId: projectTimezoneId = '',
      dateTimeFormat: projectDateTimeFormat = DateTimeFormat.Default,
      defaultLandingPage,
    } = this.activeProject

    const { projectAddress, projectTypeOptions } = this.eventsStore.appState

    this.projectName = projectName
    this.projectCode = projectCode
    this.projectLogoUrl = projectLogoUrl
    this.projectHeaderImageUrl = projectHeaderImageUrl
    this.timezoneId = projectTimezoneId
    this.dateTimeFormat = projectDateTimeFormat
    this.defaultLandingPage =
      defaultLandingPage === DefaultLandingPage.Tracker
        ? DefaultLandingPage.Home
        : defaultLandingPage || DefaultLandingPage.Home

    this.projectAddress = projectAddress
    this.seedSchedule()

    if (projectTypeOptions) {
      const {
        isDeliveriesDisabled,
        isLogisticsDisabled,
        isMaterialsDisabled,
        isMaterialResourcesDisabled,
        isPhotosDisabled,
        isFormsDisabled,
        isAnalyticsDisabled,
        isEquipmentDisabled,
        isStruxTagDisabled,
        isLocationsDisabled,
      } = projectTypeOptions

      this.isDeliveriesEnabled = !isDeliveriesDisabled
      this.isAnalyticsEnabled = !isAnalyticsDisabled
      this.isTrackerEnabled = false
      this.isLogisticsEnabled = !isLogisticsDisabled
      this.isMaterialsEnabled = !isMaterialsDisabled
      this.isMaterialResourcesEnabled = !isMaterialResourcesDisabled
      this.isPhotosEnabled = !isPhotosDisabled
      this.isFormsEnabled = !isFormsDisabled
      this.isEquipmentEnabled = !isEquipmentDisabled
      this.isStruxTagEnabled = !isStruxTagDisabled
      this.isLocationsEnabled = !isLocationsDisabled

      if (this.isDefaultLandingPageUnavailable) {
        this.setDefaultLandingPage()
      }
    }
  }

  @action.bound
  public setProjectName(newProjectName: string) {
    this.projectName = newProjectName
    newProjectName = newProjectName.trim()
    this.isProjectErrorMessageVisible =
      !projectNameValidationRegex.test(newProjectName)
  }

  @action.bound
  public setProjectLogo(logoUrl: string) {
    this.projectLogoUrl = logoUrl
  }

  @action.bound
  public setProjectHeaderImage(headerImageUrl: string) {
    this.projectHeaderImageUrl = headerImageUrl
  }

  public saveProjectName() {
    const project = Object.assign(this.activeProject.toInput(), {
      name: this.projectName,
    })
    this.saveProject(project)
  }

  public saveProjectLogo() {
    const project = Object.assign(this.activeProject.toInput(), {
      logoUrl: this.projectLogoUrl,
      headerImageUrl: this.projectHeaderImageUrl,
      isLogoWhite: null,
    })
    this.saveProject(project)
  }

  public saveProjectHeaderImage() {
    const project = Object.assign(this.activeProject.toInput(), {
      logoUrl: this.projectLogoUrl,
      headerImageUrl: this.projectHeaderImageUrl,
    })
    this.saveProject(project)
  }

  @action.bound
  public setProjectCode(newProjectCode: string) {
    const projectCode = this.getFormattedProjectCode(newProjectCode)

    this.projectCode = projectCode
    this.isProjectCodeErrorMessageVisible = !projectCode.length
  }

  @action.bound
  public setProjectAddressSuggestions(
    newProjectAddressSuggestions: IMapBoxFeature[],
  ) {
    this.projectAddressSuggestions = newProjectAddressSuggestions
  }

  @action.bound
  public setProjectAddress(newProjectAddress: IProjectAddressInput) {
    this.projectAddress = newProjectAddress
    this.timezoneId = getTimezoneIdByCoordinates(
      newProjectAddress?.center?.lng || 0,
      newProjectAddress?.center?.lat || 0,
    )
  }

  @action.bound
  public saveProjectAddress() {
    this.saveAddress()
  }

  @action.bound
  public setDateTimeFormat(dateTime: DateTimeFormat) {
    this.dateTimeFormat = dateTime
    if (!this.isNewProjectSelected) {
      this.updateProjectDateTimeFormat()
    }
  }

  @action.bound
  public setScheduleMode(mode: ProjectScheduleMode) {
    this.scheduleMode = mode
    // Only Everyday rewrites the week; Custom presents the same days as they are.
    if (mode === ProjectScheduleMode.Everyday) {
      this.daySchedules = withEverydayWeek(this.daySchedules)
    }
    this.saveSchedule()
  }

  @action.bound
  public toggleWorkingDay(dayNumber: number) {
    this.daySchedules = withToggledWorkingDay(this.daySchedules, dayNumber)
    this.saveSchedule()
  }

  @action.bound
  public setDayWorkingHours(dayNumber: number, startDate: Date, endDate: Date) {
    this.daySchedules = withDayHours(
      this.daySchedules,
      dayNumber,
      startDate.getTime(),
      endDate.getTime(),
    )
    this.saveSchedule()
  }

  @action.bound
  public setEverydayWorkingHours(startDate: Date, endDate: Date) {
    this.daySchedules = withEverydayHours(
      this.daySchedules,
      startDate.getTime(),
      endDate.getTime(),
    )
    this.saveSchedule()
  }

  @action.bound
  public setProjectClosureDates(intervals: IProjectClosedInterval[]) {
    this.closedIntervals = intervals
    if (!this.isNewProjectSelected) {
      this.updatesProjectClosures()
    }
  }

  /**
   * A new project's closures live on the store until it is created; a saved
   * one's are read straight off the project, the same split every other field
   * on this form makes.
   */
  @computed
  public get closures(): IProjectClosedInterval[] {
    const intervals = this.isNewProjectSelected
      ? this.closedIntervals
      : this.activeProject.closedIntervals

    return intervals || []
  }

  /** Replaces the closure at `index`, or appends when it is omitted. */
  @action.bound
  public upsertClosure(interval: IProjectClosedInterval, index?: number) {
    const intervals = this.closures.slice()
    if (index == null) {
      intervals.push(interval)
    } else {
      intervals[index] = interval
    }

    this.setProjectClosureDates(intervals)
  }

  @action.bound
  public removeClosure(interval: IProjectClosedInterval) {
    this.setProjectClosureDates(this.closures.filter(i => i !== interval))
  }

  @action.bound
  public setProjectDefaultLandingPage(page: DefaultLandingPage) {
    this.defaultLandingPage = page
    if (!this.isNewProjectSelected) {
      this.updateProjectDefaultLandingPage()
    }
  }

  @action.bound
  public toggleDeliveriesEnabled() {
    this.isDeliveriesEnabled = !this.isDeliveriesEnabled
    if (
      !this.isDeliveriesEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Deliveries
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleFormsEnabled() {
    this.isFormsEnabled = !this.isFormsEnabled
    if (
      !this.isFormsEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Forms
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleAnalyticsEnabled() {
    this.isAnalyticsEnabled = !this.isAnalyticsEnabled
    if (
      !this.isAnalyticsEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Analytics
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleTrackerEnabled() {
    this.isTrackerEnabled = false
    if (this.defaultLandingPage === DefaultLandingPage.Tracker) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleLogisticsEnabled() {
    this.isLogisticsEnabled = !this.isLogisticsEnabled
    if (
      !this.isLogisticsEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Home
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public togglePhotosEnabled() {
    this.isPhotosEnabled = !this.isPhotosEnabled
    if (
      !this.isPhotosEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Photos
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleMaterialsEnabled() {
    this.isMaterialsEnabled = !this.isMaterialsEnabled
    if (
      !this.isMaterialsEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Materials
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleMaterialResourcesEnabled() {
    this.isMaterialResourcesEnabled = !this.isMaterialResourcesEnabled
    if (
      !this.isMaterialResourcesEnabled &&
      this.defaultLandingPage === DefaultLandingPage.MaterialResources
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleEquipmentEnabled() {
    this.isEquipmentEnabled = !this.isEquipmentEnabled
    if (
      !this.isEquipmentEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Equipment
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleStruxTagEnabled() {
    this.isStruxTagEnabled = !this.isStruxTagEnabled
    if (
      !this.isStruxTagEnabled &&
      this.defaultLandingPage === DefaultLandingPage.StruxTags
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  /** Turns the listed Resources apps on or off together, with a single save. */
  @action.bound
  public setResourceAppsEnabled(isEnabled: boolean, apps: ResourceApp[]) {
    const landingPageByApp: Record<ResourceApp, DefaultLandingPage> = {
      locations: DefaultLandingPage.Locations,
      equipment: DefaultLandingPage.Equipment,
      materialResources: DefaultLandingPage.MaterialResources,
      struxTags: DefaultLandingPage.StruxTags,
    }
    apps.forEach(app => {
      switch (app) {
        case 'locations':
          this.isLocationsEnabled = isEnabled
          break
        case 'equipment':
          this.isEquipmentEnabled = isEnabled
          break
        case 'materialResources':
          this.isMaterialResourcesEnabled = isEnabled
          break
        case 'struxTags':
          this.isStruxTagEnabled = isEnabled
          break
      }
    })
    if (
      !isEnabled &&
      apps.some(app => landingPageByApp[app] === this.defaultLandingPage)
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public toggleLocationsEnabled() {
    this.isLocationsEnabled = !this.isLocationsEnabled
    if (
      !this.isLocationsEnabled &&
      this.defaultLandingPage === DefaultLandingPage.Locations
    ) {
      this.setDefaultLandingPage()
    }
    this.setTypeOptions()
  }

  @action.bound
  public submitForm(
    additionalSaveEvent?: (projectId: string) => Promise<void>,
  ) {
    this.createProject(additionalSaveEvent)

    this.disableNewProjectSelection()
  }

  @action.bound
  public enableNewProjectSelection() {
    this.isNewProjectSelected = true

    // The schedule is about to hold new-project defaults, so it stops belonging
    // to any project. Left set, the key would tell `seedSchedule` the tab is
    // already showing the project the user came from, and returning to that same
    // project would keep the defaults instead of reloading its saved week — and
    // then autosave, reading the same key, would write them over it.
    this.scheduleProjectId = null
  }

  @action.bound
  public disableNewProjectSelection() {
    this.isNewProjectSelected = false
  }

  @action.bound
  public selectProject(project: Project) {
    this.disableNewProjectSelection()
    this.projectsStore.selectProject(project).then(() => {
      this.setProjectDataValues()
      this.resetErrors()
    })
  }

  @action.bound
  public onNewProjectSelected() {
    this.enableNewProjectSelection()
    this.clearForm()
    this.resetErrors()
    this.setDefaultSchedule()
    this.setDefaultProjectTimezoneAndLocation()
    this.projectSetUpPageStore.navigateTo(ProjectSetUpPage.PROJECT_CREATION)
  }

  @action
  public resetProjectCode() {
    this.projectCode = ''
  }

  public copySignUpLinkToClipboard = () => {
    window.navigator.clipboard.writeText(this.projectSignUpUrl)

    toast.success(Localization.translator.copiedToClipboard)
  }

  public get projectSignUpUrl(): string {
    const path = getTransitionPath(
      commonRoutes.SIGN_UP,
      this.activeProject.code,
    )

    return location.origin + path
  }

  public get homeUrl(): string {
    return (
      location.origin +
      getTransitionPath(commonRoutes.HOME, this.activeProject.code)
    )
  }

  public get formsUrl(): string {
    return (
      location.origin +
      getTransitionPath(commonRoutes.FORMS, this.activeProject.code)
    )
  }

  /**
   * Seeds the week with the default window and no day worked — the project
   * creation page's starting point, which leaves `workingDays` empty exactly as
   * it did before per-day hours existed.
   */
  @action.bound
  public setDefaultProjectWorkingHours() {
    this.daySchedules = this.emptySchedule
  }

  @action.bound
  private setTypeOptions() {
    this.setDefaultToggle()

    if (!this.isNewProjectSelected) {
      this.updateProjectDefaultLandingPage()
      this.saveTypeOptions()
    }
  }

  /** The whole week worked on the project's default window. */
  @action.bound
  private setDefaultSchedule() {
    this.daySchedules = withEverydayWeek(this.emptySchedule)
    this.scheduleMode = ProjectScheduleMode.Everyday
  }

  /** Every weekday off, on the project's default window. */
  private get emptySchedule(): IProjectDaySchedule[] {
    return buildProjectSchedule([], this.defaultWorkingHours)
  }

  /**
   * Loads the week from the project, once per project.
   *
   * Deliberately not re-run on every project update, unlike the rest of this
   * form: the tab saves as you edit, so each change comes straight back as a
   * PROJECT_RECEIVED, and rebuilding from that payload would drop any edit made
   * while the previous save was still in flight — and reset the Everyday/Custom
   * radio under the user, since the mode is inferred from the data rather than
   * stored. New-project mode is left alone too: there the schedule holds
   * defaults the user is still filling in.
   */
  @action.bound
  private seedSchedule() {
    if (
      this.isNewProjectSelected ||
      this.scheduleProjectId === this.activeProject.id
    ) {
      return
    }

    this.scheduleProjectId = this.activeProject.id
    this.daySchedules = buildProjectSchedule(
      getScheduleWorkingDays(this.activeProject.workingSchedule),
      this.projectDateStore.projectWorkingHours,
      this.activeProject.workingSchedule,
    )
    this.scheduleMode = getProjectScheduleMode(
      this.daySchedules,
      this.projectDateStore.getDaySeconds,
    )
  }

  private setDefaultProjectTimezoneAndLocation = () => {
    const { latitude, longitude } = this.eventsStore.appState
    if (latitude && longitude) {
      this.timezoneId = getTimezoneIdByCoordinates(longitude, latitude)
      this.projectAddress.center = {
        lat: latitude,
        lng: longitude,
      }
    } else {
      this.timezoneId = defaultTimezoneId
    }
  }

  private clearForm() {
    const state = this.eventsStore.appState
    this.projectName = ''
    this.projectCode = ''
    this.projectLogoUrl = ''
    this.projectAddress = state.getDefaultProjectAddress()
    const {
      isDeliveriesDisabled,
      isLogisticsDisabled,
      isMaterialsDisabled,
      isMaterialResourcesDisabled,
      isFormsDisabled,
      isEquipmentDisabled,
      isStruxTagDisabled,
      isLocationsDisabled,
    } = state.getDefaultProjectTypeOptions()
    this.isDeliveriesEnabled = !isDeliveriesDisabled
    this.isFormsEnabled = !isFormsDisabled
    this.isTrackerEnabled = false
    this.isLogisticsEnabled = !isLogisticsDisabled
    this.isMaterialsEnabled = !isMaterialsDisabled
    this.isMaterialResourcesEnabled = !isMaterialResourcesDisabled
    this.isEquipmentEnabled = !isEquipmentDisabled
    this.isStruxTagEnabled = !isStruxTagDisabled
    this.isLocationsEnabled = !isLocationsDisabled
    this.timezoneId = ''
    this.dateTimeFormat = DateTimeFormat.Default
  }

  private setDefaultToggle = () => {
    if (
      !this.isDeliveriesEnabled &&
      !this.isLogisticsEnabled &&
      !this.isMaterialsEnabled &&
      !this.isMaterialResourcesEnabled &&
      !this.isPhotosEnabled &&
      !this.isFormsEnabled &&
      !this.isAnalyticsEnabled &&
      !this.isEquipmentEnabled &&
      !this.isStruxTagEnabled &&
      !this.isLocationsEnabled
    ) {
      this.isLogisticsEnabled = true
    }
  }

  private getProjectTypeOptions(
    id: string,
    projectId: string,
  ): IProjectTypeOptionsInput {
    return {
      id,
      projectId,
      isDeliveriesDisabled: !this.isDeliveriesEnabled,
      isTrackerDisabled: true,
      isLogisticsDisabled: !this.isLogisticsEnabled,
      isMaterialsDisabled: !this.isMaterialsEnabled,
      isMaterialResourcesDisabled: !this.isMaterialResourcesEnabled,
      isEquipmentDisabled: !this.isEquipmentEnabled,
      isPhotosDisabled: !this.isPhotosEnabled,
      isFormsDisabled: !this.isFormsEnabled,
      isAnalyticsDisabled: !this.isAnalyticsEnabled,
      isStruxTagDisabled: !this.isStruxTagEnabled,
      isLocationsDisabled: !this.isLocationsEnabled,
    }
  }

  private async createProject(
    additionalSaveEvent?: (projectId: string) => Promise<void>,
  ): Promise<string> {
    const project = Object.assign(new Project(), this.updatedFields, {
      ...toProjectScheduleInput(this.daySchedules),
      setUpFinished: [PAGE_NAME],
      defaultLandingPage: this.defaultLandingPage,
      closedIntervals: this.closedIntervals,
    })

    const projectHistory = {
      lastUpdated: [this.lastUpdated],
      projectId: EMPTY_OBJECT_ID,
    } as IProjectHistory
    projectHistory.lastUpdated?.forEach(lu => delete lu.updateInfo.schedule)

    const projectData = {
      project: project.toInput(),
      address: Object.assign(this.projectAddress, {
        projectId: EMPTY_OBJECT_ID,
      }),
      typeOptions: this.getProjectTypeOptions(null, EMPTY_OBJECT_ID),
      history: projectHistory,
    }

    const {
      data: { saveManyProjectsData },
    } = await this.graphExecutorStore.mutate(SaveProjectsDataDocument, {
      projectsData: [projectData],
    })

    const [savedProject] = saveManyProjectsData

    this.projectsStore.setOne(savedProject)

    const projectId = savedProject.id
    if (additionalSaveEvent) {
      await additionalSaveEvent(projectId)
    }

    this.eventsStore.dispatch(e.ACTIVATE_PROJECT_SUCCESS, savedProject)

    return savedProject.id
  }

  private saveProject(
    project: Project | IProjectInput,
    hasTimezoneChanged?: boolean,
  ): Promise<string> {
    project.workingHours = project.workingHours || this.defaultWorkingHours

    return new Promise((resolve, reject) => {
      this.eventsStore.dispatch(
        e.SAVE_PROJECT,
        project,
        {
          onSuccessCb: resolve,
          hasTimezoneChanged,
        } as ISaveProjectSuccessOptions,
        reject,
      )
    })
  }

  private saveTypeOptions(
    newProjectId?: string,
    shouldCompleteRequest?: boolean,
  ): Promise<IMutation> {
    return new Promise((resolve, reject) => {
      const { id: projectTypeOptionId } =
        this.eventsStore.appState.projectTypeOptions
      const { id: projectId } = this.activeProject
      this.eventsStore.dispatch(
        e.SAVE_PROJECT_TYPE_OPTIONS,
        this.getProjectTypeOptions(
          newProjectId ? null : projectTypeOptionId,
          newProjectId || projectId,
        ),
        resolve,
        reject,
        shouldCompleteRequest,
      )
    })
  }

  private saveAddress(
    newProjectId?: string,
    shouldCompleteRequest?: boolean,
  ): Promise<IMutation> {
    return new Promise((resolve, reject) => {
      const { id: projectAddressId } = this.eventsStore.appState.projectAddress
      const { id: projectId } = this.activeProject
      this.eventsStore.dispatch(
        e.SAVE_PROJECT_ADDRESSES,
        [
          Object.assign(this.projectAddress, {
            id: newProjectId ? null : projectAddressId,
            projectId: newProjectId || projectId,
          }),
        ],
        resolve,
        reject,
        shouldCompleteRequest,
      )
    })
  }

  private updateProjectDateTimeFormat() {
    const project = Object.assign(this.activeProject.toInput(), {
      dateTimeFormat: this.dateTimeFormat,
    })
    this.saveProject(project)
  }

  /**
   * The Time tab has no Save button — every schedule edit persists as it is
   * made. Debounced because each save is a whole-project mutation whose response
   * refreshes `activeProject`: setting up a custom week is a burst of a dozen
   * toggles, and those should cost one round trip, not a dozen overlapping ones.
   */
  private saveSchedule = customDebounce(() => {
    // The callback reads the project and the schedule when the timer fires, not
    // when the edit was made, so a project switched inside the debounce window
    // would otherwise write one project's week onto another. `scheduleProjectId`
    // already records which project the loaded schedule belongs to, so a
    // mismatch means the edit is stale — dropped rather than misfiled.
    if (
      this.isNewProjectSelected ||
      this.scheduleProjectId !== this.activeProject?.id
    ) {
      return
    }

    const project = Object.assign(
      this.activeProject.toInput(),
      toProjectScheduleInput(this.daySchedules),
    )
    this.saveProject(project)
  }, SCHEDULE_SAVE_DEBOUNCE_MS)

  private updateProjectDefaultLandingPage() {
    const project = Object.assign(this.activeProject.toInput(), {
      defaultLandingPage: this.defaultLandingPage,
    })
    this.saveProject(project)
  }

  private updatesProjectClosures() {
    const project = Object.assign(this.activeProject.toInput(), {
      closedIntervals: this.closedIntervals,
    })
    this.saveProject(project)
  }

  private updateProject() {
    const hasTimezoneChanged = this.timezoneId !== this.activeProject.timezoneId
    const project = Object.assign(
      this.activeProject.toInput(),
      this.updatedFields,
    )
    this.projectSetUpPageStore.setPageLastUpdated(this.lastUpdated)
    this.projectSetUpPageStore.saveCurrentProjectHistory()

    this.saveAddress()
    this.saveTypeOptions()
    this.saveProject(project, hasTimezoneChanged)
  }

  private get isDefaultLandingPageUnavailable(): boolean {
    switch (this.defaultLandingPage) {
      case DefaultLandingPage.Home:
        return !this.isLogisticsEnabled
      case DefaultLandingPage.Deliveries:
        return !this.isDeliveriesEnabled
      case DefaultLandingPage.Tracker:
        return true
      case DefaultLandingPage.Materials:
        return !this.isMaterialsEnabled
      case DefaultLandingPage.MaterialResources:
        return !this.isMaterialResourcesEnabled
      case DefaultLandingPage.Photos:
        return !this.isPhotosEnabled
      case DefaultLandingPage.Forms:
        return !this.isFormsEnabled
      case DefaultLandingPage.Analytics:
        return !this.isAnalyticsEnabled
      case DefaultLandingPage.Equipment:
        return !this.isEquipmentEnabled
      case DefaultLandingPage.StruxTags:
        return !this.isStruxTagEnabled
      case DefaultLandingPage.Locations:
        return !this.isLocationsEnabled
      default:
        return false
    }
  }

  private setDefaultLandingPage() {
    switch (true) {
      case this.isLogisticsEnabled:
        this.defaultLandingPage = DefaultLandingPage.Home
        break
      case this.isDeliveriesEnabled:
        this.defaultLandingPage = DefaultLandingPage.Deliveries
        break
      case this.isMaterialsEnabled:
        this.defaultLandingPage = DefaultLandingPage.Materials
        break
      case this.isMaterialResourcesEnabled:
        this.defaultLandingPage = DefaultLandingPage.MaterialResources
        break
      case this.isPhotosEnabled:
        this.defaultLandingPage = DefaultLandingPage.Photos
        break
      case this.isFormsEnabled:
        this.defaultLandingPage = DefaultLandingPage.Forms
        break
      case this.isAnalyticsEnabled:
        this.defaultLandingPage = DefaultLandingPage.Analytics
        break
      case this.isEquipmentEnabled:
        this.defaultLandingPage = DefaultLandingPage.Equipment
        break
      case this.isStruxTagEnabled:
        this.defaultLandingPage = DefaultLandingPage.StruxTags
        break
      case this.isLocationsEnabled:
        this.defaultLandingPage = DefaultLandingPage.Locations
        break
    }
  }

  @action.bound
  private resetErrors() {
    this.isProjectErrorMessageVisible = false
    this.isProjectCodeErrorMessageVisible = false
  }

  private getFormattedProjectCode(bareProjectCode: string): string {
    return bareProjectCode
      .replace(WITHOUT_SPECIAL_CHARACTERS_PATTERN, '')
      .slice(0, projectCodeMaxLength)
      .toUpperCase()
  }
}
